import React, { useEffect, useReducer } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import visualData from 'assets/pictures/observatory/visual-data.svg'
import CustomizationParameters from './CustomizationParameters'
import { ModuleHabsContext, ModuleHabsDispatchContext } from './AdminModulesApp'
import { OBSERVATORY } from './ObservatoryConstants'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import UserAction from '../user/actions/UserAction'
import ProgressCard from 'components/card/ProgressCard'
import { isNil, isObject, partition } from 'lodash'
import ToastrAction from 'toastr/actions/ToastrAction'

const initialHabsState = {
    habilitationsUser: [],
    parameters: [],
    moduleParameters: [],
}

const habsReducer = (state, action) => {
    switch (action.type) {
        case 'habilitationsUser':
            return { ...state, habilitationsUser: action.payload }
        case 'parameters':
            return { ...state, parameters: action.payload }
        case 'moduleParameters':
            return { ...state, moduleParameters: action.payload }
        case 'reset':
            return initialHabsState
        default:
            return state
    }
}

const GlobalDialog = ({
    open = false,
    onClose = () => {},
}) => {
    const {
        userHabilitations,
    } = useSelector(store => ({
        userHabilitations: store.UserReducer.userHabilitations,
    }), shallowEqual)

    const dispatch = useDispatch()

    const [habsState, habsDispatch] = useReducer(habsReducer, initialHabsState)

    useEffect(() => {
        if (open && userHabilitations.length && !habsState.habilitationsUser.length) {
            habsDispatch({ type: 'habilitationsUser', payload: userHabilitations })
        }
    }, [open, userHabilitations, habsState.habilitationsUser.length])

    useEffect(() => {
        if (!open) {
            habsDispatch({ type: 'reset' })
            dispatch(AdministrationAction.fetchGlobalParameters())
        }
    }, [open])

    const { isLoaded, progress } = useProgressDispatch(() => open ? [
        dispatch(UserAction.fetchUser(OBSERVATORY.name)),
        dispatch(AdministrationAction.fetchUserSettings(OBSERVATORY.name, true)),
        dispatch(UserAction.fetchApplicationHabilitations()),
    ] : [], [open])

    const onValidate = () => {
        const [parametersToDelete, otherParameters] = partition(habsState.parameters.filter(p => !isNil(p.isNew)), param => isNil(param.value))
        const [newParameters, parameters] = partition(otherParameters.map(p => ({ ...p, value: isObject(p.value) ? JSON.stringify(p.value) : `${p.value}` })), param => param.isNew)
        if (newParameters.length) {
            dispatch(AdministrationAction.createGlobalParameters(newParameters))
        }
        if (parameters.length) {
            dispatch(AdministrationAction.updateGlobalParameters(parameters))
        }
        if (parametersToDelete.length) {
            dispatch(AdministrationAction.deleteGlobalParameters(parametersToDelete))
        }

        if (parameters.length
            || parametersToDelete.length) {
            dispatch(ToastrAction.success(i18n.moduleSuccessfullySaved))
        }
        onClose()
    }

    return (
        <Dialog
            open={open}
            maxWidth='xl'
            slotProps={{
                paper: {
                    sx: {
                        minHeight: '85vh', maxHeight: '85vh',
                    },
                },
            }}
        >
            <DefaultDialogTitle
                title={i18n.globalParametersModification}
                onClose={onClose}
            />
            <DialogContent sx={{ flex: '1 1 0' }}>
                <Grid container sx={{ height: '100%' }} alignItems='center' columnSpacing={0.5}>
                    <Grid container size='grow' sx={{ height: '100%', maxHeight: '100%', overflowY: 'auto', paddingRight: 1 }} rowSpacing={1.5}>
                        {isLoaded ? (
                            <ModuleHabsContext.Provider value={habsState}>
                                <ModuleHabsDispatchContext.Provider value={habsDispatch}>
                                    <CustomizationParameters module={{ global: true, module: OBSERVATORY.name }} />
                                </ModuleHabsDispatchContext.Provider>
                            </ModuleHabsContext.Provider>
                        ) : (
                            <Grid size={12}><ProgressCard progress={progress} /></Grid>
                        )}
                    </Grid>
                    <Grid size={2.75} sx={{ '& object': { height: 'auto', width: '100%' } }}>
                        <object data={visualData} type='image/svg+xml' />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={onValidate}
                >
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

GlobalDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
}

export default GlobalDialog